import { detectionsHaveSpread } from "../shared/catalog";
import { humanEvidenceLine, panelEvidence } from "../shared/evidence-copy";
import { eventClassLabel, eventClassOf } from "../shared/event-state";
import { bearingDeg, CLUSTER_MAX_GAP_MS, countryName, nearbyIncidents } from "../shared/geo";
import { fwiClass, type FwiSample } from "../shared/fwi";
import { observationWorldviewUrl } from "../shared/imagery";
import { groupObservations, selectObservationLog } from "../shared/observe";
import { S3_SOURCE } from "../shared/s3-list";
import { canPromoteToObserved } from "../shared/score";
import { frpAtOrBeforeSeries, frpEventSeries, type FrpPoint } from "../shared/series";
import type { Detection, EvidenceLine, Incident, IncidentDetail, WebcamLink, WindSample } from "../shared/types";
import {
  formatAge,
  formatCardinal,
  formatClock,
  formatClockShort,
  formatDelta,
  formatFirePercent,
  formatFrp,
  formatLag,
  formatChartSpan,
  formatLookClock,
  formatNearbySeen,
  frpChartTitle,
  observationLinkLabel,
  sourceLabel,
} from "./format";
import { bindFrpHover, frpPlayheadX, renderFrpChart, setFrpPlayheadLine } from "./frp-chart";
import { startReplay } from "./replay";
import { windVaneSvg } from "./wind-mark";

type PanelEl = HTMLElement & {
  _stopReplay?: () => void;
  _fwi?: FwiSample | null;
  _fwiId?: string;
  /** Cached so Play does not collapse detections → series every frame. */
  _frpSeries?: FrpPoint[];
  _frpKey?: string;
};

const TIPS = {
  frp: "Fire Radiative Power — heat the satellite measured, in megawatts (MW). Larger = more intense fire.",
  mw: "Megawatts. Unit for Fire Radiative Power.",
  delta: "Change in FRP since the previous satellite overpass of this fire.",
  lastObs: "Newest satellite pixel in this cluster, relative to now — not the playhead.",
  lastRet: "When IGNIS last stored a new pixel for this fire.",
  lag: "Delay from the satellite observation until IGNIS ingested that pixel (FIRMS latency).",
  detections: "Hotspot pixels clustered into this fire — not the number of satellite overpasses.",
  eventClass: "How sure we are this is a fire. Tap to jump to Details. Suspected = one look. Likely = a strong satellite pass. Observed = two different satellites.",
  follow: "Browser and email alerts for this fire.",
  copyLink: "Copy a link to this fire.",
  copyCoords: "Copy latitude and longitude.",
  osm: "Open this place in OpenStreetMap.",
  evidence: "Why this is Suspected, Likely, or Observed. A second satellite (including Sentinel-3) can confirm. Weather cannot.",
  wind: "10 m wind at this location from Open-Meteo. We refresh while this panel is open. The timestamp is the model step, not when IGNIS fetched it.",
};

export function stopPanel(root: HTMLElement): void {
  (root as PanelEl)._stopReplay?.();
  (root as PanelEl)._stopReplay = undefined;
}

export function applyWindUpdate(root: HTMLElement, wind: WindSample | null, nowMs: number): void {
  const slot = root.querySelector("#wind-slot");
  if (slot) slot.innerHTML = windInner(wind, nowMs);
}

export function applyCamerasUpdate(root: HTMLElement, cameras: WebcamLink[]): void {
  const slot = root.querySelector("#cameras-slot");
  if (slot) slot.innerHTML = camerasHtml(cameras);
}

export function applyFwiUpdate(root: HTMLElement, sample: FwiSample | null): void {
  const panel = root as PanelEl;
  // Keep the last sample so a panel re-paint does not flash "FWI —".
  if (sample) panel._fwi = sample;
  const slot = root.querySelector("#fwi-slot");
  if (slot) slot.innerHTML = fwiInner(panel._fwi ?? sample);
}

/** Move the chart line and FRP cell with the map playhead — do not rebuild the panel. */
export function applyPlayheadUpdate(
  root: HTMLElement,
  opts: { playheadMs: number; untilMs: number; detections: Detection[]; liveFrp: number; archive?: boolean; nowMs?: number; historical?: boolean },
): void {
  const live = !opts.archive && !opts.historical && opts.playheadMs >= opts.untilMs - 30_000;
  const nowMs = opts.nowMs ?? Date.now();
  const panel = root as PanelEl;
  const key = opts.detections[0]?.incident_id ?? `${opts.detections.length}:${opts.detections[0]?.id ?? ""}`;
  // Rebuild the SVG only when the open fire changes — Play just slides the line.
  if (panel._frpKey !== key || !panel._frpSeries) {
    panel._frpSeries = frpEventSeries(opts.detections);
    panel._frpKey = key;
    const slot = root.querySelector("#frp-chart-slot");
    if (slot) slot.innerHTML = renderFrpChart(panel._frpSeries, opts.playheadMs, nowMs);
    bindFrpHover(slot);
  } else {
    setFrpPlayheadLine(root.querySelector("#frp-chart-slot"), frpPlayheadX(panel._frpSeries, opts.playheadMs));
  }
  const series = panel._frpSeries;
  const titleEl = root.querySelector("#frp-chart-title");
  if (titleEl) titleEl.textContent = frpChartTitle(series, nowMs);
  const vital = frpVitalAtPlayhead({
    archive: Boolean(opts.archive),
    live,
    playheadMs: opts.playheadMs,
    detections: opts.detections,
    series,
    liveFrp: opts.liveFrp,
  });
  const strong = root.querySelector("#frp-vital strong");
  const label = root.querySelector("#frp-vital span");
  if (strong) strong.textContent = formatFrp(vital.mw);
  if (label) label.textContent = vital.label;
}

/** Last measured MW + a label that does not pretend an S3 look had intensity. */
export function frpVitalAtPlayhead(opts: {
  archive: boolean;
  live: boolean;
  playheadMs: number;
  detections: Detection[];
  series: FrpPoint[];
  liveFrp: number;
}): { mw: number | null; label: string } {
  const measured = frpAtOrBeforeSeries(opts.series, opts.playheadMs);
  let newest: Detection | null = null;
  for (const d of opts.detections) {
    const t = Date.parse(d.observed_at);
    if (!Number.isFinite(t) || t > opts.playheadMs) continue;
    if (!newest || t > Date.parse(newest.observed_at)) newest = d;
  }
  const s3Clock = newest?.source === S3_SOURCE;
  const mw = measured ?? (opts.live || opts.archive ? opts.liveFrp : null);
  if (opts.archive) return { mw, label: "last look" };
  if (s3Clock) {
    const lastT = [...opts.series].reverse().find((p) => p.t <= opts.playheadMs);
    return {
      mw,
      label: lastT ? `last measured ${formatClockShort(new Date(lastT.t).toISOString())}` : "last measured",
    };
  }
  if (opts.live) return { mw, label: "FRP now" };
  return { mw: measured, label: `as of ${formatClockShort(new Date(opts.playheadMs).toISOString())}` };
}

export function renderPanel(
  root: HTMLElement,
  detail: IncidentDetail,
  opts: {
    following: boolean;
    nowMs: number;
    email: string | null;
    nearby: Incident[];
    playheadMs: number;
    untilMs: number;
    /** Theater close returns to the catalog — label the control Back. */
    back?: boolean;
    historical?: boolean;
  },
): void {
  const panel = root as PanelEl;
  panel._stopReplay?.();
  if (panel._fwiId !== detail.incident.id) {
    panel._fwi = null;
    panel._fwiId = detail.incident.id;
  }

  const { incident, detections, wind } = detail;
  const delta = formatDelta(incident.frp_delta_mw, incident.frp_delta_pct);
  const title = detail.place_name || countryName(incident.country);
  const series = frpEventSeries(detections);
  // Same cache applyPlayheadUpdate uses — a full panel paint already drew the SVG.
  panel._frpSeries = series;
  panel._frpKey = detections[0]?.incident_id ?? `${detections.length}:${detections[0]?.id ?? ""}`;
  const chartTitle = frpChartTitle(series, opts.nowMs);
  // Same first–last span as the FRP chart — not a rolling 24 h clip.
  const replaySpanLabel = formatChartSpan(series, opts.nowMs);
  const replayCaption = replaySpanLabel ? `${replaySpanLabel}.` : "This fire.";
  const replayAria = replaySpanLabel
    ? `Heat replay of this fire, ${replaySpanLabel}, sped up`
    : "Heat replay of this fire, sped up";
  const archive = Boolean(detail.archive);
  const live = !archive && !opts.historical && opts.playheadMs >= opts.untilMs - 30_000;
  const { mw: frpShow, label: frpLabel } = frpVitalAtPlayhead({
    archive,
    live,
    playheadMs: opts.playheadMs,
    detections,
    series,
    liveFrp: incident.frp_mw,
  });

  const canReplay = !archive || detectionsHaveSpread(detections);
  const thumbs = archive && !canReplay && detail.imagery.length <= 1 ? "" : thumbsHtml(detail.imagery);
  const cams = camerasHtml(detail.cameras ?? []);
  const lastLook = detail.last_observed || incident.last_seen;
  const archiveNote = `Last look ${formatLookClock(lastLook, opts.nowMs)} · no longer on the live map`;
  const hero = canReplay
    ? replayCanvasHtml(replayAria)
    : archiveHeroHtml(detail, archiveNote);

  // One pass per row — Details explains the class, the log lists the looks.
  const groups = groupObservations(detections);
  const rows = observationLogRows(detections, opts.nowMs);

  const nearbyListed = nearbyIncidents(opts.nearby, incident);
  const nearbyRows = nearbyListed
    .map((n) => {
      const brng = formatCardinal(bearingDeg(incident.lat, incident.lon, n.lat, n.lon));
      return `<li>
        <button type="button" class="nearby-fire" data-id="${escapeHtml(n.id)}">
          <span class="nearby-name" data-lat="${n.lat}" data-lon="${n.lon}" data-cc="${escapeHtml(n.country)}">${escapeHtml(countryName(n.country))}</span>
          <span>${(n.distance_m / 1000).toFixed(1)} km ${escapeHtml(brng)}</span>
          <span>${escapeHtml(formatNearbySeen(n.last_seen, opts.nowMs, CLUSTER_MAX_GAP_MS))}</span>
        </button>
      </li>`;
    })
    .join("");

  const obsAge = opts.nowMs - Date.parse(detail.last_observed);
  const showLag = Number.isFinite(obsAge) && obsAge < CLUSTER_MAX_GAP_MS;

  // One bell: browser notifications plus the email popup. Link / pin / OSM have tips.
  const followTip = opts.email ? `Alerts on. Email goes to ${opts.email}.` : TIPS.follow;
  panel.innerHTML = `
    <button type="button" class="close" id="panel-close" aria-label="${opts.back ? "Back" : "Close"}" data-tip="${opts.back ? "Back" : "Close"}">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="${opts.back ? "M15 6l-6 6 6 6" : "M6 6l12 12M18 6L6 18"}"/></svg>
    </button>
    ${hero}
    ${archive ? `<p class="replay-caption archive-note">${escapeHtml(archiveNote)}</p>` : `<p class="replay-caption">${escapeHtml(replayCaption)}</p>`}
    <h2>${escapeHtml(title)}</h2>
    <div class="panel-toolbar">
      <button type="button" class="event-class ${eventClassOf(incident.event_status)}" id="panel-class" data-tip="${escapeHtml(TIPS.eventClass)}" aria-label="Jump to details">${escapeHtml(eventClassLabel(eventClassOf(incident.event_status)))} · ${escapeHtml(formatFirePercent(incident.fire_probability))}</button>
      <div class="panel-actions">
        <button type="button" class="follow ${opts.following ? "on" : ""}${opts.email ? " has-email" : ""}" id="panel-follow" data-id="${escapeHtml(incident.id)}" data-tip="${escapeHtml(followTip)}" aria-label="${opts.following ? "Edit alerts for this fire" : "Get alerts for this fire"}">${ICON_FOLLOW}</button>
        <button type="button" id="panel-copy-link" data-tip="${escapeHtml(TIPS.copyLink)}" aria-label="Copy link">${ICON_LINK}</button>
        <button type="button" id="panel-copy-coords" data-tip="${escapeHtml(TIPS.copyCoords)}" aria-label="Copy coordinates" data-coords="${incident.lat.toFixed(4)}, ${incident.lon.toFixed(4)}">${ICON_PIN}</button>
        <a href="https://www.openstreetmap.org/?mlat=${incident.lat}&amp;mlon=${incident.lon}#map=12/${incident.lat}/${incident.lon}" target="_blank" rel="noreferrer" data-tip="${escapeHtml(TIPS.osm)}" aria-label="OpenStreetMap">${ICON_MAP}</a>
      </div>
    </div>
    <div class="vitals">
      ${archive ? "" : `<div id="fwi-slot">${fwiInner(panel._fwi ?? null)}</div>`}
      <div class="vital" id="frp-vital" data-tip="${escapeHtml(TIPS.frp)}">
        <strong>${formatFrp(frpShow)}</strong>
        <span>${escapeHtml(frpLabel)}</span>
      </div>
      ${
        archive
          ? ""
          : `<div class="vital" data-tip="${escapeHtml(TIPS.delta)}">
        <strong>${escapeHtml(delta)}</strong>
        <span>since last pass</span>
      </div>`
      }
    </div>
    <p class="legend-title" id="frp-chart-title">${escapeHtml(chartTitle)}</p>
    <div id="frp-chart-slot">${renderFrpChart(series, opts.playheadMs, opts.nowMs)}</div>
    <dl class="metrics">
      <div data-tip="${escapeHtml(TIPS.lastObs)}"><dt>Last observation</dt><dd>${escapeHtml(formatAge(detail.last_observed, opts.nowMs))} · ${escapeHtml(formatLookClock(detail.last_observed, opts.nowMs))}</dd></div>
      <div data-tip="${escapeHtml(TIPS.lastRet)}"><dt>Last retrieved</dt><dd>${detail.last_retrieved ? escapeHtml(formatAge(detail.last_retrieved, opts.nowMs)) : "—"}</dd></div>
      <div data-tip="${escapeHtml(TIPS.detections)}"><dt>Hotspots</dt><dd>${incident.detection_count}</dd></div>
      ${
        showLag
          ? `<div class="lag" data-tip="${escapeHtml(TIPS.lag)}"><dt>Ingest lag</dt><dd>${escapeHtml(formatLag(detail.ingest_lag_ms))}</dd></div>`
          : ""
      }
    </dl>
    <div id="wind-slot">${windInner(wind, opts.nowMs)}</div>
    ${thumbs}
    <div id="cameras-slot">${cams}</div>
    ${
      nearbyRows
        ? `<p class="legend-title">Nearby fires</p>
           <p class="fine">Separate events within 6 km.</p>
           <ul class="nearby">${nearbyRows}</ul>`
        : ""
    }
    ${evidenceHtml(panelEvidence(incident.evidence, { passCount: groups.length, platformCount: new Set(groups.map((g) => g.platform)).size, canPromoteToObserved: canPromoteToObserved(groups) }))}
    ${rows ? `<p class="legend-title">Satellite looks</p><ul class="timeline">${rows}</ul>` : ""}

  `;

  const canvas = panel.querySelector("#replay") as HTMLCanvasElement | null;
  // Archive with saved heat plays that fire's tape. Centroid-only fallback stays a still.
  if (canvas && canReplay) panel._stopReplay = startReplay(canvas, detections, opts.nowMs);
  fillNearbyPlaceNames(panel);

  panel.querySelectorAll(".thumb img").forEach((el) => {
    el.addEventListener("error", () => {
      (el.closest(".thumb") as HTMLElement | null)?.classList.add("hidden");
    });
  });
  bindFrpHover(panel.querySelector("#frp-chart-slot"));
}

/** One pass per row — two VIIRS pixels at 12:49 are one look, not two. */
function observationLogRows(detections: Detection[], nowMs: number): string {
  return selectObservationLog(groupObservations(detections))
    .map((g) => {
      const sample = detections.find((d) => d.source === g.platform && d.observed_at === g.observed_at) ?? detections.find((d) => d.source === g.platform);
      const href = sample ? observationWorldviewUrl(sample) : "#";
      // EFFIS Sentinel-3 has no FRP — never show a megawatt, even if a heat proxy leaked in.
      const frp =
        g.platform === "S3" ? `seen · ${g.n} px` : `${formatFrp(g.frp_max)} · ${g.n} px`;
      return `<li data-source="${escapeHtml(g.platform)}">
        <span>${escapeHtml(formatLookClock(g.observed_at, nowMs))}</span>
        <a href="${escapeHtml(href)}" target="_blank" rel="noreferrer"${g.platform === "S3" ? ' title="NASA Worldview true colour at this time — no Sentinel-3 FRP layer"' : ""}>${escapeHtml(observationLinkLabel(g.platform))}</a>
        <span data-tip="${escapeHtml(TIPS.mw)}">${escapeHtml(frp)}</span>
      </li>`;
    })
    .join("");
}

/** One line per log-odds term, in ordinary language. */
function evidenceHtml(lines: EvidenceLine[] | null | undefined): string {
  if (!lines?.length) return "";
  const items = lines
    .map((e) => {
      const mark = e.polarity === "against" ? "−" : e.polarity === "prior" ? "·" : "+";
      return `<li class="${escapeHtml(e.polarity)}"><i>${mark}</i><span>${escapeHtml(humanEvidenceLine(e))}</span></li>`;
    })
    .join("");
  return `<section id="panel-details" class="panel-details">
    <p class="legend-title" data-tip="${escapeHtml(TIPS.evidence)}">Details</p>
    <ul class="evidence">${items}</ul>
  </section>`;
}

// Bell / envelope match the stroke weight of the existing toolbar icons.
const ICON_FOLLOW = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.5 9.5a5.5 5.5 0 0 1 11 0c0 5.2 2.2 7 2.2 7H4.3s2.2-1.8 2.2-7"/><path d="M10 19.2a2 2 0 0 0 4 0"/></svg>`;
const ICON_LINK = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5.93"/><path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 7.07 7.07L14 18.07"/></svg>`;
const ICON_PIN = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s7-6.2 7-11.2A7 7 0 0 0 5 9.8C5 14.8 12 21 12 21z"/><circle cx="12" cy="9.8" r="2.2"/></svg>`;
const ICON_MAP = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4z"/><path d="M9 4v13M15 6.5v13"/></svg>`;

function replayCanvasHtml(replayAria: string): string {
  return `<canvas id="replay" class="replay notranslate" width="696" height="336" translate="no" aria-label="${escapeHtml(replayAria)}"></canvas>`;
}

function archiveHeroHtml(detail: IncidentDetail, note: string): string {
  const img = detail.imagery[0];
  if (img) {
    const cap = `${sourceLabel(img.source)} · ${formatClock(img.observed_at)}${img.night ? " · night" : ""}`;
    return `<button type="button" class="replay archive-hero thumb" data-full="${escapeHtml(img.full_url)}" data-caption="${escapeHtml(cap)}" aria-label="${escapeHtml(note)}">
      <img src="${escapeHtml(img.thumb_url)}" alt="${escapeHtml(cap)}" />
    </button>`;
  }
  return `<div class="replay archive-hero" role="img" aria-label="${escapeHtml(note)}"></div>`;
}

function thumbsHtml(imagery: IncidentDetail["imagery"]): string {
  if (imagery.length === 0) return "";
  const thumbs = imagery
    .map((img) => {
      const cap = `${sourceLabel(img.source)} · ${formatClock(img.observed_at)}${img.night ? " · night" : ""}`;
      return `<button type="button" class="thumb" data-full="${escapeHtml(img.full_url)}" data-caption="${escapeHtml(cap)}">
        <img src="${escapeHtml(img.thumb_url)}" alt="${escapeHtml(cap)}" />
        <span>${escapeHtml(sourceLabel(img.source))}${img.night ? " · night" : ""}</span>
      </button>`;
    })
    .join("");
  return `<p class="legend-title">Satellite snapshots</p>
    <div class="thumbs" id="thumbs">${thumbs}</div>`;
}

function camerasHtml(cameras: WebcamLink[]): string {
  if (cameras.length === 0) return "";
  const items = cameras
    .map(
      (c) => `<li>
        <a href="${escapeHtml(c.url)}" target="_blank" rel="noreferrer">
          <span>${escapeHtml(c.name)}</span>
          <span>${c.km < 10 ? c.km.toFixed(1) : Math.round(c.km)} km</span>
        </a>
      </li>`,
    )
    .join("");
  return `<p class="legend-title">Nearby cameras</p>
    <ul class="cameras">${items}</ul>
    <p class="fine">Public OSM webcams within 15 km. Feeds are not ours and may be offline.</p>`;
}

function windInner(wind: WindSample | null, nowMs: number): string {
  if (!wind) return "";
  const toDeg = (wind.direction_deg + 180) % 360;
  return `<div class="wind-card" data-tip="${escapeHtml(`${TIPS.wind} Last model step: ${formatClock(wind.observed_at)}.`)}">
    <div class="wind-compass" aria-hidden="true">
      <span class="wind-n">N</span>
      <div class="wind-compass-vane" style="transform: rotate(${toDeg}deg)">${windVaneSvg("panel")}</div>
    </div>
    <div>
      <strong>${Math.round(wind.speed_kmh)} km/h</strong>
      <span>from ${formatCardinal(wind.direction_deg)}</span>
      <span class="meta">pushing ${formatCardinal(toDeg)}</span>
      <span class="meta">updated ${escapeHtml(formatAge(wind.observed_at, nowMs))}</span>
    </div>
  </div>`;
}

function fwiInner(sample: FwiSample | null): string {
  if (!sample) {
    return `<div class="vital"><strong>FWI —</strong><span>weather danger</span></div>`;
  }
  const cls = fwiClass(sample.fwi);
  return `<div class="vital" data-tip="ECMWF Fire Weather Index at this coordinate, from Copernicus EFFIS. Weather-based danger, not a fire outline.">
    <strong style="color:${cls.color}">FWI ${sample.fwi.toFixed(1)}</strong>
    <span>${escapeHtml(cls.label)}${sample.isi != null ? ` · ISI ${sample.isi.toFixed(1)}` : ""}</span>
  </div>`;
}

/** Swap country fallback for a Nominatim locality once it lands. */
function fillNearbyPlaceNames(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(".nearby-name").forEach((el) => {
    const lat = Number(el.dataset.lat);
    const lon = Number(el.dataset.lon);
    const cc = el.dataset.cc ?? "";
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    void fetch(`/api/place?lat=${lat}&lon=${lon}&cc=${encodeURIComponent(cc)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { name?: string } | null) => {
        if (d?.name) el.textContent = d.name;
      })
      .catch(() => {
        // Keep the country label if Nominatim is slow or down.
      });
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}
